/**
 * @file Renders the repeatable tier list input for a project's investment classes.
 */
import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Space, Tooltip, Typography } from 'antd';

import ListInput from '@components/Form/Input/ListInput';
import { getConfig } from '@components/Config';
import { TIER_NAMES } from '@components/Domain';
import { isBackstage } from '@components/Constants';
import { getIdentityEmail } from '@components/Authorization/authenticate';
import {
  TYPE_STRING,
  TYPE_CHECKBOX,
  TYPE_NUMBER,
  TYPE_TEXT,
} from '@components/Form/FormItem/types';

import './TiersListInput.css';

const LABEL_NAME = 'Name';
const LABEL_INTERNAL = 'Internal';
const LABEL_DESCRIPTION = 'Description';
const LABEL_INTERNAL_TOOLTIP =
  'Internal classes are not visible to investors in the soft commitment form.';

const TEMPORARY_ALLOWED_TIER_PERCENTAGE_EMAILS = getConfig<string[]>(
  'TEMPORARY_ALLOWED_TIER_PERCENTAGE_EMAILS',
);

type FormPath = string[]

type Props = {
  form: unknown;
  name: FormPath;
  path: FormPath;
  title: string;
  addTitle: string;
}

type OutletContext = {
  isOrganizationController: () => boolean;
}

/**
 * Renders the list input for a project's tiers, with the name, return percentage, description and internal fields.
 */
const TiersListInput = ({
  form,
  name,
  path,
  title,
  addTitle,
}: Props) => {
  const { isOrganizationController } = useOutletContext<OutletContext>();

  const max = TIER_NAMES.length;
  const onlyLastDelete = true;

  // The authorization identity carries the email of the user group for a
  // console user, therefore the authentication token supplies the email.
  const userEmail = getIdentityEmail(isBackstage);

  const hasUserEmail = !!userEmail;

  const isTemporaryAllowedUser =
    hasUserEmail &&
    TEMPORARY_ALLOWED_TIER_PERCENTAGE_EMAILS.includes(userEmail);

  const canEditReturnPercentage =
    isOrganizationController() || isTemporaryAllowedUser;

  const schema = [
    {
      name: 'title',
      type: TYPE_STRING,
      required: true,
      maxLength: 64,
      itemProps: { noStyle: true },
      inputProps: {
        style: { width: '25%' },
        prefix: (
          <Typography.Text type="secondary">
            <span className="enum" />
          </Typography.Text>
        ),
      },
      placeholder: LABEL_NAME,
    },
    {
      name: 'returnPercentage',
      type: TYPE_NUMBER,
      itemProps: { noStyle: true },
      initialValue: 0,
      inputProps: {
        style: { width: '12%' },
        /**
         * Strips the percent sign from the input value before it is stored.
         */
        parser: (value?: string) => value?.replace('%', ''),
        /**
         * Appends a percent sign to the stored value for display.
         */
        formatter: (value?: string | number) => `${value}%`,
        min: 0,
        step: 0.1,
        disabled: !canEditReturnPercentage,
      },
    },
    {
      name: 'description',
      type: TYPE_TEXT,
      required: true,
      maxLength: 256,
      itemProps: { noStyle: true },
      inputProps: { style: { width: '63%' }, autoSize: { minRows: 1 } },
      placeholder: LABEL_DESCRIPTION,
    },
    {
      name: 'isPrivate',
      type: TYPE_CHECKBOX,
      required: false,
      itemProps: { noStyle: true },
      inputProps: {
        style: { marginTop: 6 },
        label: (
          <Space>
            {LABEL_INTERNAL}
            <Tooltip title={LABEL_INTERNAL_TOOLTIP}>
              <Typography.Text type="secondary">
                <QuestionCircleOutlined />
              </Typography.Text>
            </Tooltip>
          </Space>
        ),
      },
    },
  ];

  const listInputProps = {
    min: 1,
    className: 'horizontal-oriented',
    max,
    form,
    name,
    path,
    title,
    schema,
    addTitle,
    onlyLastDelete,
  };

  return (
    <div className="tiers-list-input" style={{ marginBottom: 24 }}>
      <ListInput {...listInputProps} />
    </div>
  );
};

export default TiersListInput;
